// Presents the game via PixiJS: GPU world renderer (default) or software framebuffer fallback.

import { Application, Container, Sprite, Texture, SCALE_MODES } from 'pixi.js';
import { screen } from './surface';
import { physPal, consumePalDirty } from './palette';
import { SCREEN_W, SCREEN_H, RES_SCALE, TILE } from '../core/constants';
import { profilerMeasure } from '../dev/profiler';
import { isGpuRenderer } from './render-config';
import { computeCamera, type Camera } from './camera';
import { initGpuRenderer, getGpuWorldRoot, setGpuCamera, syncPaletteFilter } from './gpu/renderer';
import { G } from '../core/state';
import { isCoarsePointer } from '../ui/device';

const MIN_UI_PX = 12;
const MIN_SCALE = MIN_UI_PX / (8 * RES_SCALE);
const MAX_SCALE = 4;

export type ViewLayout = {
  scale: number;
  offX: number;
  offY: number;
  vw: number;
  vh: number;
  canvasW: number;
  canvasH: number;
  camera: Camera;
};

let app: Application;
let displayRoot: HTMLElement;
let fbCanvas: HTMLCanvasElement;
let fbCtx: CanvasRenderingContext2D;
let imageData: ImageData;
let fbSprite: Sprite;
let fbTexture: Texture;
const palLut = new Uint32Array(256);
let out32: Uint32Array;

const layoutListeners: Array<(v: ViewLayout) => void> = [];

export let overlay: Container;

export const view: ViewLayout = {
  scale: 1,
  offX: 0,
  offY: 0,
  vw: 0,
  vh: 0,
  canvasW: 0,
  canvasH: 0,
  camera: computeCamera({ w: SCREEN_W, h: SCREEN_H }, 0, 0),
};

let dungeonGpuActive = false;
let softwareFrameNeeded = false;

export function getApp(): Application {
  return app;
}

export function onViewLayout(fn: (v: ViewLayout) => void): void {
  layoutListeners.push(fn);
  fn(view);
}

export function setDungeonGpuActive(active: boolean): void {
  dungeonGpuActive = active && isGpuRenderer();
  if (getGpuWorldRoot()) {
    getGpuWorldRoot()!.visible = dungeonGpuActive;
  }
  fbSprite.visible = !dungeonGpuActive || softwareFrameNeeded;
}

export function markSoftwareFrame(): void {
  softwareFrameNeeded = true;
  fbSprite.visible = true;
}

export function getLayoutCamera(scrollX = 0, scrollY = 0): Camera {
  return computeCamera({ w: view.vw || window.innerWidth, h: view.vh || window.innerHeight }, scrollX, scrollY);
}

function rebuildPalLut(): void {
  for (let i = 0; i < 256; i++) {
    const v = i * 3;
    palLut[i] = 0xff000000 | (physPal[v + 2] << 16) | (physPal[v + 1] << 8) | physPal[v];
  }
}

function onLayoutEvent(): void {
  layout();
}

export async function initDisplay(root: HTMLElement): Promise<void> {
  if (app) {
    window.removeEventListener('resize', onLayoutEvent);
    window.visualViewport?.removeEventListener('resize', onLayoutEvent);
    window.visualViewport?.removeEventListener('scroll', onLayoutEvent);
    document.removeEventListener('fullscreenchange', onLayoutEvent);
    document.removeEventListener('webkitfullscreenchange', onLayoutEvent);
    app.destroy(true);
    root.querySelectorAll('canvas').forEach((c) => c.remove());
  }

  displayRoot = root;
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  app = new Application();
  await app.init({
    backgroundColor: 0x000000,
    antialias: false,
    resolution: dpr,
    autoDensity: true,
    powerPreference: 'high-performance',
  });
  app.ticker.stop();
  root.appendChild(app.canvas);

  fbCanvas = document.createElement('canvas');
  fbCanvas.width = SCREEN_W;
  fbCanvas.height = SCREEN_H;
  fbCtx = fbCanvas.getContext('2d', { willReadFrequently: false })!;
  imageData = fbCtx.createImageData(SCREEN_W, SCREEN_H);
  out32 = new Uint32Array(imageData.data.buffer);

  fbTexture = Texture.from(fbCanvas);
  fbTexture.source.scaleMode = SCALE_MODES.NEAREST ?? 'nearest';
  fbSprite = new Sprite(fbTexture);
  app.stage.addChild(fbSprite);

  if (isGpuRenderer()) {
    initGpuRenderer(app.stage);
    getGpuWorldRoot()!.visible = false;
  }

  overlay = new Container();
  app.stage.addChild(overlay);

  rebuildPalLut();
  window.addEventListener('resize', onLayoutEvent);
  window.visualViewport?.addEventListener('resize', onLayoutEvent);
  window.visualViewport?.addEventListener('scroll', onLayoutEvent);
  document.addEventListener('fullscreenchange', onLayoutEvent);
  document.addEventListener('webkitfullscreenchange', onLayoutEvent);
  layout();
}

function viewportRect(): { vw: number; vh: number; offLeft: number; offTop: number } {
  const vv = window.visualViewport;
  if (vv) {
    return { vw: vv.width, vh: vv.height, offLeft: vv.offsetLeft, offTop: vv.offsetTop };
  }
  return { vw: window.innerWidth, vh: window.innerHeight, offLeft: 0, offTop: 0 };
}

function layout(): void {
  const { vw, vh, offLeft, offTop } = viewportRect();

  if (displayRoot) {
    displayRoot.style.position = 'fixed';
    displayRoot.style.inset = 'auto';
    displayRoot.style.right = 'auto';
    displayRoot.style.bottom = 'auto';
    displayRoot.style.left = `${offLeft}px`;
    displayRoot.style.top = `${offTop}px`;
    displayRoot.style.width = `${vw}px`;
    displayRoot.style.height = `${vh}px`;
  }
  const scrollX = typeof G?.scroll_x === 'number' ? G.scroll_x : 0;
  const scrollY = typeof G?.scroll_y === 'number' ? G.scroll_y : 0;

  let canvasW: number;
  let canvasH: number;
  const mobile = isCoarsePointer();

  if (isGpuRenderer()) {
    const cam = computeCamera({ w: vw, h: vh }, scrollX, scrollY);
    if (mobile) {
      canvasW = vw;
      canvasH = vh;
      view.camera = cam;
      view.scale = cam.pixelScale;
      setGpuCamera(cam);
      if (getGpuWorldRoot()) {
        getGpuWorldRoot()!.scale.set(cam.pixelScale);
        getGpuWorldRoot()!.position.set(-cam.coverCropX, -cam.coverCropY);
      }
      fbSprite.scale.set(cam.viewW / SCREEN_W, cam.viewH / SCREEN_H);
    } else {
      const fit = Math.min(vw / cam.viewW, vh / cam.viewH);
      const rawScale = fit;
      const displayScale = Math.max(rawScale, Math.round(TILE * rawScale) / TILE);
      canvasW = Math.min(vw, Math.max(1, Math.floor(cam.viewW * displayScale)));
      canvasH = Math.min(vh, Math.max(1, Math.floor(cam.viewH * displayScale)));
      cam.pixelScale = Math.min(canvasW / cam.viewW, canvasH / cam.viewH);
      cam.coverCropX = 0;
      cam.coverCropY = 0;
      view.camera = cam;
      view.scale = cam.pixelScale;
      setGpuCamera(cam);
      if (getGpuWorldRoot()) {
        getGpuWorldRoot()!.scale.set(cam.pixelScale);
        getGpuWorldRoot()!.position.set(0, 0);
      }
      fbSprite.scale.set(cam.viewW / SCREEN_W, cam.viewH / SCREEN_H);
    }
  } else if (mobile) {
    const cam = computeCamera({ w: vw, h: vh }, scrollX, scrollY);
    canvasW = vw;
    canvasH = vh;
    view.camera = cam;
    view.scale = cam.pixelScale;
    fbSprite.scale.set(vw / SCREEN_W, vh / SCREEN_H);
  } else {
    const fit = Math.min(vw / SCREEN_W, vh / SCREEN_H);
    let scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, fit));
    if (SCREEN_W * scale > vw || SCREEN_H * scale > vh) {
      scale = Math.min(MAX_SCALE, fit);
    }
    canvasW = Math.max(1, Math.floor(SCREEN_W * scale));
    canvasH = Math.max(1, Math.floor(SCREEN_H * scale));
    view.camera = computeCamera({ w: vw, h: vh }, scrollX, scrollY);
    view.scale = scale;
    fbSprite.scale.set(1);
  }

  app.renderer.resize(canvasW, canvasH);
  fbSprite.position.set(0, 0);

  const canvas = app.canvas as HTMLCanvasElement;
  if (mobile) {
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.margin = '0';
    if (displayRoot) {
      displayRoot.style.alignItems = 'stretch';
      displayRoot.style.justifyContent = 'stretch';
    }
  } else {
    canvas.style.width = `${canvasW}px`;
    canvas.style.height = `${canvasH}px`;
    canvas.style.margin = 'auto';
    if (displayRoot) {
      displayRoot.style.alignItems = 'center';
      displayRoot.style.justifyContent = 'center';
    }
  }
  canvas.style.display = 'block';
  canvas.style.maxWidth = 'none';
  canvas.style.maxHeight = 'none';

  const rect = canvas.getBoundingClientRect();
  view.offX = rect.left;
  view.offY = rect.top;
  view.vw = vw;
  view.vh = vh;
  view.canvasW = canvasW;
  view.canvasH = canvasH;
  overlay.position.set(0, 0);

  for (const fn of layoutListeners) fn(view);
}

export function setCanvasVisible(visible: boolean): void {
  if (!app?.canvas) return;
  const canvas = app.canvas as HTMLCanvasElement;
  canvas.style.display = visible ? 'block' : 'none';
}

export function requestGameFullscreen(): void {
  const el = displayRoot ?? document.getElementById('game-root');
  if (!el) return;
  const req =
    el.requestFullscreen?.bind(el) ??
    (el as HTMLElement & { webkitRequestFullscreen?: () => Promise<void> }).webkitRequestFullscreen?.bind(el);
  void req?.();
}

export function viewportToGame(clientX: number, clientY: number): { x: number; y: number } {
  const cam = view.camera;
  const localX = (clientX - view.offX) / cam.pixelScale;
  const localY = (clientY - view.offY) / cam.pixelScale;
  return { x: localX, y: localY };
}

function uploadSoftwareFramebuffer(): void {
  if (consumePalDirty()) rebuildPalLut();
  const src = screen.pix;
  const lut = palLut;
  for (let i = 0; i < src.length; i++) {
    out32[i] = lut[src[i]];
  }
  fbCtx.putImageData(imageData, 0, 0);
  fbTexture.source.update();
}

export function present(): void {
  profilerMeasure('present', () => {
    if (dungeonGpuActive && isGpuRenderer()) {
      profilerMeasure('gpu.present', () => {
        syncPaletteFilter();
        app.render();
      });
      softwareFrameNeeded = false;
      return;
    }

    uploadSoftwareFramebuffer();
    app.render();
    softwareFrameNeeded = false;
  });
}
